import { useEffect, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import If from 'global/ErrorHandlers/If';
import Checkbox from 'global/Securities/components/Checkbox';

import useTermsAndConditionStore from 'global/Securities/stores/useTermsAndConditionStore';
import useUIConfigStore from 'stores/useUIConfigStore';

import {
  clevertapDayTradeTNCViewed,
  clevertapDayTradeActivationSubmitted,
} from 'lib/analytics/securities';

import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import type { AcceptancesPayload } from 'global/Securities/types/api/termsAndConditions';
import { TnCFeaturesIDEnum } from 'global/Securities/types/termsAndCondition';

import useTradingStore from 'features/transaction/useTradingStore';
import {
  ActionWrapper,
  AgreementTextWrapper,
  AgreementWrapper,
  Container,
  NextButton,
  TnCFrame,
  TnCFrameWrapper,
} from './terms-condition.styled';

const TITLE = 'Syarat & Ketentuan Day Trade dan Trading Limit';

const DayTradeTncModal = ({
  title = TITLE,
  onSubmit = () => {},
  onCancel = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [tncAgreed, setTncAgreed] = useState<boolean>(false);
  const [tncUrl, setTncUrl] = useState<string>('');

  const isSubmitting = useTermsAndConditionStore((s) => s.isSubmitting);
  const termsData = useTermsAndConditionStore((s) => s.termsData);

  const postAcceptances = useTermsAndConditionStore((s) => s.postAcceptances);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const onSubmitDayTradeTnC = useTradingStore((s) => s.onSubmitDayTradeTnC);

  const FEATURE_ID = TnCFeaturesIDEnum.DAY_TRADE;
  const terms = termsData?.[FEATURE_ID];
  const isButtonDisabled = !tncAgreed || isSubmitting;

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    setTncAgreed(event.target.checked);
  };

  const handleTncSubmit = async () => {
    if (!tncAgreed) {
      return;
    }

    const acceptanceLetters: AcceptancesPayload[] = Object.keys(termsData).map(
      (key) => ({
        feature_id: key,
        is_accepted: true,
        version: termsData[key].version as number,
      }),
    );

    await postAcceptances(acceptanceLetters).then((data) => {
      if (data) {
        setUIValue('dayTradeTnc', true);
        setUIValue('dayTradeSubAccountActive', true);
        onSubmitDayTradeTnC?.();
        onSubmit();
      }
    });

    clevertapDayTradeActivationSubmitted();
  };

  const handleScrollTo = (target: string) => {
    const cleanUrl = tncUrl.split('#')[0];

    setTncUrl(`${cleanUrl}#`);

    setTimeout(() => {
      setTncUrl(`${cleanUrl}#${target}`);
    });
  };

  const handleClickDayTrade = () => {
    handleScrollTo('tnc-day-trade');
  };

  const handleClickTradingLimit = () => {
    handleScrollTo('tnc-trading-limit');
  };

  const renderClickableText = (text: string, onClick: () => void) => (
    <span onClick={onClick} aria-hidden>
      {text}
    </span>
  );

  useEffect(() => {
    if (terms?.url) {
      setTncUrl(terms.url);
    }
  }, [terms, setTncUrl]);

  useEffect(() => {
    clevertapDayTradeTNCViewed();
  }, []);

  return (
    <BaseSecuritiesModal title={title} onCancel={onCancel} {...props}>
      <Container>
        <TnCFrameWrapper>
          <If condition={tncUrl}>
            <TnCFrame title="day-trade-tnc" src={tncUrl} id="tnc-frame" />
          </If>
        </TnCFrameWrapper>

        <AgreementWrapper>
          <Checkbox onChange={handleCheckboxChange} />

          <AgreementTextWrapper>
            Saya menyetujui Syarat & Ketentuan Penggunaan{' '}
            {renderClickableText('Day Trade', handleClickDayTrade)} dan{' '}
            {renderClickableText('Trading Limit', handleClickTradingLimit)} di
            atas.
          </AgreementTextWrapper>
        </AgreementWrapper>

        <ActionWrapper>
          <NextButton
            onClick={handleTncSubmit}
            type="primary"
            disabled={isButtonDisabled}
            loading={isSubmitting}
          >
            Lanjut
          </NextButton>
        </ActionWrapper>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default DayTradeTncModal;
